// RevealPage.tsx — what the model has learned about the viewer.
//
// Scope: this page shows the signed-in rater their own embedding and their own
// ordering. It cannot show anyone else's, because the API has no endpoint that
// returns one. The population section is the only shared thing here, and it is
// aggregate by construction — directions in preference space, not people.
import { useState, useEffect } from 'react'
import Card from 'react-bootstrap/Card'
import Table from 'react-bootstrap/Table'
import Badge from 'react-bootstrap/Badge'
import Spinner from 'react-bootstrap/Spinner'
import Alert from 'react-bootstrap/Alert'
import { BsCompass, BsDiagram3, BsListOl, BsRulers } from 'react-icons/bs'
import { getReveal, getAxes, type Reveal, type Axes, type AxisView } from '../api'

const RevealPage = () => {
    const [reveal, setReveal] = useState<Reveal | null>(null)
    const [axes, setAxes] = useState<Axes | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')

    useEffect(() => {
        let live = true
        void (async () => {
            try {
                // Both at once: the axes call is independent of the reveal and
                // waiting for them in series doubles the time to first paint.
                const [r, a] = await Promise.all([getReveal(), getAxes()])
                if (!live) return
                setReveal(r)
                setAxes(a)
            } catch (e) {
                if (live) setError(e instanceof Error ? e.message : 'Could not load your embedding')
            } finally {
                if (live) setLoading(false)
            }
        })()
        return () => { live = false }
    }, [])

    if (loading) {
        return (
            <div className="d-flex justify-content-center py-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading…</span>
                </Spinner>
            </div>
        )
    }
    if (error) {
        return <div className="prose-column"><Alert variant="danger">{error}</Alert></div>
    }
    if (!reveal) return null

    return (
        <div>
            <div className="mb-4">
                <h1 className="fs-4 mb-1">Reveal</h1>
                <p className="text-body-secondary mb-0 prose-column">
                    Everything below is fitted from your own comparisons and is visible
                    only to you.
                </p>
            </div>

            {reveal.cold && (
                <Alert variant="info" className="prose-column">
                    <Alert.Heading className="fs-6">Still warming up</Alert.Heading>
                    You have made {reveal.judged} comparison{reveal.judged === 1 ? '' : 's'}.
                    Below about {reveal.coldBelow} the numbers here are mostly the model's
                    starting guess rather than anything you have said, so read them as a
                    sketch.
                </Alert>
            )}

            <div className="card-grid mb-4">
                <Card>
                    <Card.Header className="d-flex align-items-center gap-2">
                        <BsRulers /> Your Preference Vector
                    </Card.Header>
                    <Card.Body>
                        <p className="small text-body-secondary prose-column">
                            The direction your taste points in the model's latent space. The
                            coordinates have no inherent meaning on their own — what matters
                            is which of them are large, and how they compare with other
                            people's. A bar drawn faintly is one the evidence has not pinned
                            down yet.
                        </p>
                        <VectorBars vector={reveal.vector} sd={reveal.sd} />
                    </Card.Body>
                    <Card.Footer className="d-flex justify-content-between align-items-center gap-2">
                        <span className="small text-body-secondary">
                            {reveal.judged} comparison{reveal.judged === 1 ? '' : 's'}
                        </span>
                        <span className="small text-body-secondary" title="Fitted tie threshold, in score units">
                            indifference band ±{reveal.tieThreshold.toFixed(2)}
                        </span>
                    </Card.Footer>
                </Card>

                <Card>
                    <Card.Header className="d-flex align-items-center gap-2">
                        <BsDiagram3 /> How To Read This
                    </Card.Header>
                    <Card.Body>
                        <p className="small mb-2 prose-column">
                            The ordering below is <em>stochastic</em>: each person carries a
                            score and an uncertainty, and two people whose bands overlap are
                            not really ranked against each other by anything you have said
                            yet.
                        </p>
                        <p className="small mb-0 text-body-secondary prose-column">
                            The indifference band is the model's estimate of how far apart two
                            people have to be before raters reliably call a winner. Gaps
                            smaller than it are noise.
                        </p>
                    </Card.Body>
                    <Card.Footer className="text-end small text-body-secondary">
                        fitted online, updated as you rate
                    </Card.Footer>
                </Card>
            </div>

            <Card className="mb-4">
                <Card.Header className="d-flex align-items-center gap-2">
                    <BsListOl /> Your Ordering
                </Card.Header>
                <Card.Body>
                    {reveal.ordering.length === 0 ? (
                        <p className="mb-0 text-body-secondary">
                            Nothing yet — rate a few pairs and an ordering will appear here.
                        </p>
                    ) : (
                        <Table size="sm" hover responsive className="m-0 align-middle">
                            <thead>
                                <tr>
                                    <th style={{ width: '3rem' }}>#</th>
                                    <th style={{ width: '4rem' }}>Face</th>
                                    <th style={{ width: '10rem' }}>Score</th>
                                    <th>Confidence</th>
                                    <th style={{ width: '5rem' }} className="text-end">Seen</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reveal.ordering.map((row, i) => (
                                    <tr key={row.id}>
                                        <td className="text-body-secondary">{i + 1}</td>
                                        <td>
                                            <img
                                                src={row.imageUrl}
                                                alt=""
                                                className="reveal-thumb"
                                                loading="lazy"
                                            />
                                        </td>
                                        <td className="score-value text-nowrap">
                                            {row.score >= 0 ? '+' : ''}{row.score.toFixed(2)}
                                            <span className="text-body-secondary ms-1">
                                                ±{row.sd.toFixed(2)}
                                            </span>
                                        </td>
                                        <td>
                                            {/* The band, drawn. A number ±another number is
                                                hard to compare down a column; two overlapping
                                                bars are not. */}
                                            <ScoreBand
                                                score={row.score}
                                                sd={row.sd}
                                                lo={reveal.ordering[reveal.ordering.length - 1].score}
                                                hi={reveal.ordering[0].score}
                                            />
                                        </td>
                                        <td className="text-end text-body-secondary small">{row.seen}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
                <Card.Footer className="text-end small text-body-secondary">
                    {reveal.ordering.length} shown, most preferred first
                </Card.Footer>
            </Card>

            <PopulationAxes axes={axes} />
        </div>
    )
}

// VectorBars draws the embedding as a signed bar per coordinate, with opacity
// carrying certainty: a coordinate the evidence has not determined is drawn
// faint, so a confidently-zero taste and an unknown one do not look alike.
const VectorBars = ({ vector, sd }: { vector: number[]; sd: number[] }) => {
    if (vector.length === 0) {
        return <p className="mb-0 text-body-secondary small">No vector yet.</p>
    }
    const scale = Math.max(0.2, ...vector.map(Math.abs))
    return (
        <div className="vector-bars">
            {vector.map((v, k) => {
                const width = Math.abs(v) / scale * 50
                // |value| / sd — how many standard deviations from zero this
                // coordinate is, which is exactly "how sure are we it is not
                // just noise".
                const certainty = sd[k] > 0 ? Math.min(1, Math.abs(v) / (2 * sd[k])) : 1
                return (
                    <div className="vector-row" key={k}>
                        <span className="vector-index text-body-secondary">{k}</span>
                        <div className="vector-track">
                            <div
                                className={`vector-fill ${v >= 0 ? 'pos' : 'neg'}`}
                                style={{
                                    width: `${width}%`,
                                    [v >= 0 ? 'left' : 'right']: '50%',
                                    opacity: 0.25 + 0.75 * certainty,
                                }}
                                title={`coordinate ${k}: ${v.toFixed(3)} ± ${sd[k]?.toFixed(3) ?? '?'}`}
                            />
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

// ScoreBand places a score and its uncertainty on the shared range of the
// whole ordering.
const ScoreBand = ({ score, sd, lo, hi }: { score: number; sd: number; lo: number; hi: number }) => {
    const span = Math.max(1e-6, hi - lo)
    const pct = (x: number) => Math.max(0, Math.min(100, ((x - lo) / span) * 100))
    const left = pct(score - sd)
    const right = pct(score + sd)
    return (
        <div className="score-band" title={`${score.toFixed(2)} ± ${sd.toFixed(2)}`}>
            <div
                className="score-band-fill"
                style={{ left: `${left}%`, width: `${Math.max(1.5, right - left)}%` }}
            />
        </div>
    )
}

// PopulationAxes renders step 8: the principal directions of everyone's taste,
// with whatever the vision model made of the faces at each end.
const PopulationAxes = ({ axes }: { axes: Axes | null }) => {
    if (!axes) return null

    if (!axes.ready) {
        return (
            <Card>
                <Card.Header className="d-flex align-items-center gap-2">
                    <BsCompass /> Population Axes
                </Card.Header>
                <Card.Body>
                    <p className="mb-0 text-body-secondary prose-column">
                        Not enough raters yet. Once {axes.minRaters} people have each made
                        enough comparisons, the shared directions of taste get decomposed and
                        shown here. Currently {axes.raters} qualif{axes.raters === 1 ? 'ies' : 'y'}.
                    </p>
                </Card.Body>
                <Card.Footer className="text-end small text-body-secondary">
                    {axes.raters} / {axes.minRaters} raters
                </Card.Footer>
            </Card>
        )
    }

    return (
        <>
            <h2 className="fs-5 mb-1">Population Axes</h2>
            <p className="text-body-secondary small mb-3 prose-column">
                The directions along which this population's tastes most disagree, derived
                from everyone's preference vectors. Each label is a vision model's account
                of what visibly separates the two ends — a caption for a statistical
                direction, not a finding about attractiveness.
            </p>
            <div className="card-grid">
                {axes.axes.map(a => <AxisCard key={a.rank} axis={a} />)}
            </div>
        </>
    )
}

const AxisCard = ({ axis }: { axis: AxisView }) => (
    <Card>
        <Card.Header className="d-flex align-items-center justify-content-between gap-2">
            <span className="d-flex align-items-center gap-2">
                <BsCompass /> {axis.label ?? `Axis ${axis.rank + 1}`}
            </span>
            <Badge bg="secondary" title="Share of between-rater variance explained">
                {(axis.varianceFrac * 100).toFixed(0)}%
            </Badge>
        </Card.Header>
        <Card.Body>
            {axis.description ? (
                <p className="small mb-3 prose-column">{axis.description}</p>
            ) : (
                <p className="small text-body-secondary fst-italic mb-3 prose-column">
                    Not yet described — the axis exists, but no vision model has looked at
                    the two ends.
                </p>
            )}
            <div className="axis-end">
                <span className="axis-end-label text-body-secondary small">High</span>
                <div className="axis-faces">
                    {axis.highImages.map(src => <img key={src} src={src} alt="" loading="lazy" />)}
                </div>
            </div>
            <div className="axis-end mt-2">
                <span className="axis-end-label text-body-secondary small">Low</span>
                <div className="axis-faces">
                    {axis.lowImages.map(src => <img key={src} src={src} alt="" loading="lazy" />)}
                </div>
            </div>
        </Card.Body>
        <Card.Footer className="d-flex justify-content-between align-items-center gap-2">
            <span className="small text-body-secondary">{axis.nUsers} raters</span>
            <span className="small score-value text-nowrap" title="Where your own taste falls on this axis">
                you: {axis.projection >= 0 ? '+' : ''}{axis.projection.toFixed(2)}
            </span>
        </Card.Footer>
    </Card>
)

export default RevealPage
